import { cn } from "lib/utils";
import { forwardRef } from "react";
import ReactSelect, {
  type SelectInstance,
  type StylesConfig,
} from "react-select";

export type SelectOption = {
  value: string;
  label: string;
};

export type SelectWidth = "narrow" | "full";
export type SelectSize = "sm" | "md";

export type SelectProps = {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
  name?: string;
  placeholder?: string;
  width?: SelectWidth;
  size?: SelectSize;
  isDisabled?: boolean;
};

const widthClass: Record<SelectWidth, string> = {
  narrow: "w-40",
  full: "w-full",
};

/**
 * react-select の style を size (sm / md) に応じて生成する。
 * DaisyUI input-sm / input-md の高さ (32px / 40px) に合わせる。
 * @param {SelectSize} size サイズ指定
 * @return {StylesConfig} react-select styles
 */
export const buildSelectStyles = <Option, IsMulti extends boolean = false>(
  size: SelectSize,
): StylesConfig<Option, IsMulti> => {
  const minHeight = size === "sm" ? 32 : 40;
  const fontSize = size === "sm" ? 13 : 14;
  const paddingY = size === "sm" ? 0 : 2;
  return {
    control: base => ({
      ...base,
      minHeight,
      height: minHeight,
      fontSize,
    }),
    valueContainer: base => ({
      ...base,
      paddingTop: paddingY,
      paddingBottom: paddingY,
    }),
    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: base => ({
      ...base,
      height: minHeight,
    }),
    singleValue: base => ({
      ...base,
      fontSize,
    }),
    placeholder: base => ({
      ...base,
      fontSize,
    }),
    option: (base, state) => ({
      ...base,
      fontSize,
      ...(state.isSelected && {
        backgroundColor: "oklch(var(--p))",
        color: "#ffffff",
      }),
      ...(state.isFocused &&
        !state.isSelected && {
          backgroundColor: "oklch(var(--p) / 0.1)",
        }),
      ":active": {
        backgroundColor: "oklch(var(--p) / 0.2)",
      },
    }),
  };
};

/**
 * 純粋な Select リーフ。react-select をラップし、value は string で扱う。
 * @param {SelectProps} props Select の props
 * @return {JSX.Element} Select 要素
 */
export const Select = forwardRef<SelectInstance<SelectOption>, SelectProps>(
  (
    {
      options,
      value,
      defaultValue,
      onChange,
      onBlur,
      name,
      placeholder = "選択項目",
      width = "full",
      size = "sm",
      isDisabled,
    },
    ref,
  ) => {
    const selectedOption =
      value !== undefined
        ? (options.find(o => o.value === String(value)) ?? null)
        : null;
    const defaultSelected =
      defaultValue !== undefined
        ? (options.find(o => o.value === defaultValue) ?? undefined)
        : undefined;

    return (
      <div className={cn(widthClass[width])}>
        <ReactSelect<SelectOption>
          ref={ref}
          name={name}
          options={options}
          value={selectedOption}
          defaultValue={defaultSelected}
          placeholder={placeholder}
          isDisabled={isDisabled}
          onChange={newValue => onChange?.(newValue?.value)}
          onBlur={onBlur}
          styles={buildSelectStyles<SelectOption, false>(size)}
        />
      </div>
    );
  },
);
Select.displayName = "Select";
